import { NavButton } from '@/app/shell/SideBar/SideBarNavButton';
import type { SideBarItem } from '@/app/shell/SideBar/sideBarItem';
import { useTrans } from '@/shared/hooks/useTranslation';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import type React from 'react';

function groupByGroup(items: SideBarItem[]): [string, SideBarItem[]][] {
  const grouped = items.reduce(
    (acc, item) => {
      const group = item.group ?? 'default';
      acc[group] ??= [];
      acc[group].push(item);
      return acc;
    },
    {} as Record<string, SideBarItem[]>,
  );
  return Object.entries(grouped);
}

function groupId(groupName: string, idPrefix: string): string {
  const safeGroupName = groupName.replace(/[^a-zA-Z0-9_-]/g, '-');
  return `${idPrefix}-${safeGroupName}`;
}

export function NavList({
  items,
  activeItem,
  onItemClick,
  onClose,
  groupLabels,
  collapsibleGroups,
  openGroups,
  onToggleGroup,
  activeGroup,
  idPrefix,
  collapsed,
  onHoverLabel,
}: {
  readonly items: SideBarItem[];
  readonly activeItem?: string | undefined;
  readonly onItemClick: (item: SideBarItem) => void;
  readonly onClose?: (() => void) | undefined;
  readonly groupLabels?: Record<string, string> | undefined;
  readonly collapsibleGroups?: readonly string[] | undefined;
  readonly openGroups?: Readonly<Record<string, boolean>> | undefined;
  readonly onToggleGroup?: ((groupName: string) => void) | undefined;
  readonly activeGroup?: string | undefined;
  readonly idPrefix: string;
  readonly collapsed?: boolean | undefined;
  readonly onHoverLabel?: ((label: string | null) => void) | undefined;
}): React.JSX.Element {
  const { t } = useTrans();
  const groups = groupByGroup(items);
  const firstSecondaryGroupIndex = groups.findIndex(
    ([name]) => groupLabels?.[name] && collapsibleGroups?.includes(name),
  );
  const firstSecondaryGroupName = groups[firstSecondaryGroupIndex]?.[0];
  return (
    <nav className="flex flex-1 flex-col gap-5 overflow-y-auto p-4">
      {groups.map(([groupName, groupEntryItems]) => (
        <div key={groupName} className="flex flex-col gap-0.5">
          {!collapsed && firstSecondaryGroupIndex > 0 && groupName === firstSecondaryGroupName && (
            <hr className="mx-2 -mt-3 mb-1 h-px border-0 bg-border/60" />
          )}
          {(() => {
            const label = groupLabels?.[groupName];
            const isCollapsible =
              !collapsed && Boolean(label) && (collapsibleGroups?.includes(groupName) ?? false);
            const isOpen =
              collapsed || !isCollapsible || (openGroups?.[groupName] ?? activeGroup === groupName);
            const itemsId = groupId(groupName, idPrefix);
            const navButtons = groupEntryItems.map((item) => (
              <NavButton
                key={item.key}
                item={item}
                isActive={activeItem === item.key}
                onItemClick={onItemClick}
                onClose={onClose}
                collapsed={collapsed}
                onHoverLabel={onHoverLabel}
              />
            ));

            return (
              <>
                {label &&
                  !collapsed &&
                  (isCollapsible ? (
                    <button
                      type="button"
                      aria-label={
                        isOpen
                          ? t('dataset.sidebar.collapseGroup', { group: label })
                          : t('dataset.sidebar.expandGroup', { group: label })
                      }
                      aria-expanded={isOpen}
                      {...(isOpen ? { 'aria-controls': itemsId } : {})}
                      onClick={() => onToggleGroup?.(groupName)}
                      className="flex w-full cursor-pointer items-center justify-between rounded-md px-2.5 py-2 text-left font-display text-2xs font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:bg-surface-secondary hover:text-foreground"
                    >
                      <span>{label}</span>
                      <motion.span
                        animate={{ rotate: isOpen ? 0 : -90 }}
                        transition={{ duration: 0.18, ease: 'easeInOut' }}
                        className="flex items-center justify-center"
                      >
                        <ChevronDown size={14} />
                      </motion.span>
                    </button>
                  ) : (
                    <div className="px-2.5 pb-1 font-display text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {label}
                    </div>
                  ))}
                {isCollapsible ? (
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key={itemsId}
                        id={itemsId}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-0.5 pt-0.5">{navButtons}</div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                ) : (
                  <div id={itemsId} className="flex flex-col gap-0.5">
                    {navButtons}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      ))}
    </nav>
  );
}
